import React from 'react';
import { Button, Box, Typography } from '@mui/material';

interface AppleSignInButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  text?: 'Sign in with Apple' | 'Sign up with Apple' | 'Continue with Apple';
  fullWidth?: boolean;
  variant?: 'black' | 'white' | 'white-outlined';
}

const AppleSignInButton: React.FC<AppleSignInButtonProps> = ({
  onClick,
  disabled = false,
  loading = false,
  text = 'Sign in with Apple',
  fullWidth = true,
  variant = 'black'
}) => {
  const AppleIcon = ({ color }: { color: string }) => (
    <>
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill={color}
        style={{ marginRight: '8px' }}
        aria-hidden="true"
      >
        <path d="M13.3034 8.29688C13.3135 7.20312 13.7135 6.32812 14.5034 5.66406C13.9534 4.86719 13.1234 4.42969 12.0134 4.35156C10.9534 4.24219 9.78344 5.00781 9.33344 5.00781C8.86344 5.00781 7.86344 4.39062 6.96344 4.39062C5.16344 4.42969 3.25344 5.76562 3.25344 8.52344C3.25344 9.36719 3.42344 10.2422 3.76344 11.1484C4.20344 12.3125 5.85344 15.5391 7.71344 15.4688C8.55344 15.4375 9.14344 14.8906 10.2034 14.8906C11.2334 14.8906 11.7634 15.4688 12.7134 15.4688C14.5934 15.4375 16.0934 12.5234 16.5134 11.3594C14.3634 10.4844 13.3034 8.29688 13.3034 8.29688ZM11.5834 3.14062C12.3334 2.22656 12.2634 1.39062 12.2434 1.07812C11.5534 1.11719 10.7434 1.53906 10.2734 2.05469C9.76344 2.60156 9.46344 3.28906 9.52344 4.04688C10.2634 4.09375 10.9634 3.69531 11.5834 3.14062Z" />
      </svg>
      <span className="sr-only">Apple icon</span>
    </>
  );

  const getButtonStyles = () => {
    let backgroundColor: string;
    let textColor: string;
    let borderColor: string;
    let hoverBackgroundColor: string;
    let activeBackgroundColor: string;

    switch (variant) {
      case 'white':
        backgroundColor = '#ffffff';
        textColor = '#000000';
        borderColor = 'transparent';
        hoverBackgroundColor = '#f5f5f5';
        activeBackgroundColor = '#e5e5e5';
        break;
      case 'white-outlined':
        backgroundColor = '#ffffff';
        textColor = '#000000';
        borderColor = '#000000';
        hoverBackgroundColor = '#f5f5f5';
        activeBackgroundColor = '#e5e5e5';
        break;
      case 'black':
      default:
        backgroundColor = '#000000';
        textColor = '#ffffff';
        borderColor = 'transparent';
        hoverBackgroundColor = '#1a1a1a';
        activeBackgroundColor = '#333333';
        break;
    }

    return {
      backgroundColor,
      color: textColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '6px', // Apple's recommended corner radius
      textTransform: 'none' as const,
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif',
      fontWeight: 600, // Medium weight as per Apple guidelines
      fontSize: '16px', // Apple's recommended font size
      height: '44px', // Apple's default and recommended button height
      minWidth: fullWidth ? '100%' : '200px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
      },
      '&:active': {
        backgroundColor: activeBackgroundColor,
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(0, 122, 255, 0.3)', // Apple blue focus ring
      },
      '&.Mui-disabled': {
        backgroundColor: disabled ? (variant === 'black' ? '#666666' : '#f0f0f0') : backgroundColor,
        color: disabled ? (variant === 'black' ? '#999999' : '#cccccc') : textColor,
        opacity: disabled ? 0.6 : 1,
      },
    };
  };

  const buttonStyles = getButtonStyles();
  const iconColor = variant === 'black' ? '#ffffff' : '#000000';

  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      sx={buttonStyles}
      startIcon={loading ? null : <AppleIcon color={iconColor} />}
      aria-label={text}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              aria-hidden="true"
              sx={{
                width: '18px',
                height: '18px',
                marginRight: '8px',
                border: '2px solid',
                borderColor: variant === 'black' ? '#666666' : '#cccccc',
                borderTopColor: variant === 'black' ? '#ffffff' : '#000000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif'
              }}
            >
              {text === 'Sign in with Apple' ? 'Signing in...' :
               text === 'Sign up with Apple' ? 'Signing up...' :
               'Connecting...'}
            </Typography>
          </Box>
        ) : (
          <Typography
            component="span"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif'
            }}
          >
            {text}
          </Typography>
        )}
      </Box>
    </Button>
  );
};

export default AppleSignInButton;
